import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { toRgba, getUserThemeColor } from '../../../ui/theme/colors';

const EditInput = styled.input`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_300};
  border-radius: 4px;
  padding: 2px 6px;
  margin: -3px -7px;
  width: calc(100% + 14px);
  outline: none;

  &:focus {
    border-color: ${({ $themeColor, theme }) => $themeColor || theme.colors.PRIMARY};
    box-shadow: 0 0 0 2px ${({ $themeColor, theme }) => toRgba(getUserThemeColor($themeColor || theme.colors.PRIMARY), 0.2)};
  }
`;

const DisplayWrapper = styled.span`
  cursor: ${({ $canEdit }) => ($canEdit ? 'text' : 'inherit')};
  display: inline;
`;

/**
 * Inline editable text component
 *
 * @param {Object} props
 * @param {boolean} props.isEditing - Whether currently in edit mode
 * @param {string} props.value - Current text value
 * @param {Function} props.onSave - Called with new value when editing is complete
 * @param {Function} props.onCancel - Called when editing is cancelled
 * @param {React.ReactNode} props.children - Display content when not editing
 * @param {string} props.themeColor - Theme color for focus state
 * @param {boolean} props.isLoading - Whether save is in progress
 * @param {Function} props.onStartEdit - Called when user double-clicks to start editing
 * @param {Function} props.onClick - Called on single click (delayed to allow double-click detection)
 */
const DOUBLE_CLICK_DELAY = 250;

const InlineEdit = ({ isEditing, value, onSave, onCancel, children, themeColor, isLoading = false, onStartEdit, onClick }) => {
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);
  const clickTimeoutRef = useRef(null);

  // Reset edit value when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditValue(value);
      // Focus and select all text after a short delay to ensure input is rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 0);
    }
  }, [isEditing, value]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (editValue.trim() && editValue.trim() !== value) {
          onSave(editValue.trim());
        } else {
          onCancel();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    },
    [editValue, value, onSave, onCancel],
  );

  const handleBlur = useCallback(() => {
    if (editValue.trim() && editValue.trim() !== value) {
      onSave(editValue.trim());
    } else {
      onCancel();
    }
  }, [editValue, value, onSave, onCancel]);

  const handleClick = useCallback((e) => {
    // Prevent click from propagating when in edit mode
    e.stopPropagation();
  }, []);

  const handleDoubleClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      // Cancel any pending single-click navigation
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      if (onStartEdit) {
        onStartEdit();
      }
    },
    [onStartEdit],
  );

  // Delay single-click to allow double-click detection
  const handleDisplayClick = useCallback(
    (e) => {
      e.stopPropagation();
      // Clear any existing timeout (handles the case where click fires twice during double-click)
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      // If we have an onClick handler (for navigation), delay it to detect double-click
      if (onClick) {
        clickTimeoutRef.current = setTimeout(() => {
          clickTimeoutRef.current = null;
          onClick();
        }, DOUBLE_CLICK_DELAY);
      }
    },
    [onClick],
  );

  if (!isEditing) {
    if (onStartEdit) {
      return (
        <DisplayWrapper $canEdit onDoubleClick={handleDoubleClick} onClick={handleDisplayClick}>
          {children}
        </DisplayWrapper>
      );
    }
    return children;
  }

  return (
    <EditInput
      ref={inputRef}
      type='text'
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClick={handleClick}
      disabled={isLoading}
      $themeColor={themeColor}
    />
  );
};

export default InlineEdit;
